import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, withModifiers as _withModifiers, renderSlot as _renderSlot, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, withKeys as _withKeys, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-label", "id", "onKeydown"]
const _hoisted_2 = {
  class: "modal-content",
  style: {"overflow":"visible"}
}
const _hoisted_3 = { class: "modal-header" }
const _hoisted_4 = { class: "modal-title" }
const _hoisted_5 = {
  key: 0,
  class: "modal-footer"
}

import { useId, watch, ref, nextTick } from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'InformationModal',
  props: /*@__PURE__*/_mergeModels({
    title: { default: "" },
    body: {},
    showFooter: { type: Boolean },
    className: {},
    width: { default: "750px" },
    modalBodyContainerClass: { default: "" }
  }, {
    "modelValue": { type: Boolean },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["dismiss", "submit"], ["update:modelValue"]),
  setup(__props: any, { expose: __expose, emit: __emit }) {

const computedId = useId();
const model = _useModel(__props, "modelValue");
const modalEl = ref<HTMLElement | null>(null);
const triggerElement = ref<HTMLElement | null>(null);
const props = __props;

const emit = __emit;

function handleSubmit() {
    model.value = false;
    emit("submit");
}

function handleEnter() {
    handleSubmit();
}

function showModal() {
    model.value = true;
}

function hideModal() {
    model.value = false;
    emit("dismiss");
}

const FOCUSABLE_SELECTORS = [
    "a[href]",
    "area[href]",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "button:not([disabled])",
    "iframe",
    "object",
    "embed",
    "[contenteditable]",
    "[tabindex]:not([tabindex='-1'])",
].join(",");

function getFocusableElements() {
    if (!modalEl.value) {
        return [] as HTMLElement[];
    }

    return Array.from(
        modalEl.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
    ).filter(
        (element) =>
            !element.hasAttribute("disabled") &&
            element.tabIndex !== -1 &&
            element.offsetParent !== null
    );
}

function handleTab(event: KeyboardEvent) {
    const focusable = getFocusableElements();
    const activeElement = document.activeElement as HTMLElement | null;

    if (focusable.length === 0) {
        modalEl.value?.focus();
        return;
    }

    const currentIndex = activeElement
        ? focusable.indexOf(activeElement)
        : -1;
    const lastIndex = focusable.length - 1;

    if (event.shiftKey) {
        if (currentIndex <= 0) {
            focusable[lastIndex].focus();
        } else {
            focusable[currentIndex - 1].focus();
        }
    } else {
        if (currentIndex === -1 || currentIndex === lastIndex) {
            focusable[0].focus();
        } else {
            focusable[currentIndex + 1].focus();
        }
    }
}

// Keep the internal model in sync with the default `v-model` prop (`modelValue`).
watch(
    () => (props as any).modelValue,
    (modelValue) => {
        if (modelValue !== model.value) {
            model.value = modelValue as boolean;
        }
    }
);

// Focus the modal element when it's shown so key events are received,
// and return focus to the triggering element when it's hidden.
watch(model, async (val) => {
    if (val) {
        triggerElement.value = document.activeElement as HTMLElement | null;
        await nextTick();
        modalEl.value?.focus();
    } else {
        if (triggerElement.value?.isConnected) {
            triggerElement.value.focus();
        }
        triggerElement.value = null;
    }
});

__expose({ show: showModal, hide: hideModal });

return (_ctx: any,_cache: any) => {
  return (model.value)
    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "modal-backdrop fade show" }, null, -1)),
        _createElementVNode("div", {
          class: _normalizeClass(["modal fade show overflow-inherit-y", [props.className]]),
          style: {"display":"block"},
          tabindex: "-1",
          role: "dialog",
          "aria-modal": "true",
          "aria-label": props.title,
          id: _unref(computedId),
          ref_key: "modalEl",
          ref: modalEl,
          onKeydown: [
            _withKeys(_withModifiers(handleEnter, ["prevent"]), ["enter"]),
            _withKeys(_withModifiers(hideModal, ["prevent"]), ["escape"]),
            _withKeys(_withModifiers(handleTab, ["prevent"]), ["tab"])
          ],
          onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["self","prevent"]))
        }, [
          _createElementVNode("div", {
            class: "modal-dialog modal-dialog-centered modal-dialog-scrollable",
            style: _normalizeStyle(`max-width: ${props.width}`)
          }, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("h4", _hoisted_4, _toDisplayString(props.title), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn-close",
                  "aria-label": "Close",
                  onClick: _withModifiers(hideModal, ["prevent"])
                })
              ]),
              _createElementVNode("div", {
                class: _normalizeClass(["modal-body", __props.modalBodyContainerClass])
              }, [
                _createElementVNode("p", null, [
                  _renderSlot(_ctx.$slots, "body", {
                    onDismiss: hideModal,
                    onSubmit: handleSubmit
                  }, () => [
                    _createTextVNode(_toDisplayString(props.body), 1)
                  ]),
                  _renderSlot(_ctx.$slots, "default")
                ])
              ], 2),
              (props.showFooter)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _renderSlot(_ctx.$slots, "footer", {
                      onDismiss: hideModal,
                      onSubmit: handleSubmit
                    })
                  ]))
                : _createCommentVNode("", true)
            ])
          ], 4)
        ], 42, _hoisted_1)
      ], 64))
    : _createCommentVNode("", true)
}
}

})